/**
 * Sprint 16c — shared helpers for the response→entity mapping editor.
 * Pure browser utilities: dot-path reads, sample-response key discovery,
 * field-name similarity (for auto-suggest), and a client-side field-map preview.
 */

import type { FieldDefinition } from "../../../lib/types";

/** Read a dot-path (e.g. "requester.email") from a nested object. */
export function getByPath(obj: unknown, path: string): unknown {
  if (!path) return undefined;
  let cur: unknown = obj;
  for (const seg of path.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * Flatten a sample response object into candidate dot-paths (leaf scalars +
 * one level of arrays-of-objects). Used to populate the left column from a
 * pasted sample response. Caps depth + breadth so a huge sample stays usable.
 */
export function discoverPaths(sample: unknown, maxDepth = 3): string[] {
  const out = new Set<string>();
  const walk = (node: unknown, prefix: string, depth: number) => {
    if (depth > maxDepth || node == null) return;
    if (Array.isArray(node)) {
      // Describe the element shape from the first object element.
      const first = node.find((x) => x && typeof x === "object");
      if (first) walk(first, prefix, depth);
      else if (prefix) out.add(prefix);
      return;
    }
    if (typeof node === "object") {
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (v != null && typeof v === "object") walk(v, path, depth + 1);
        else out.add(path);
      }
      return;
    }
    if (prefix) out.add(prefix);
  };
  walk(sample, "", 0);
  return [...out].slice(0, 200);
}

/** If the sample is a collection-wrapping object, return the array key (e.g. "tickets"). */
export function detectCollectionPath(sample: unknown): string | null {
  if (!sample || typeof sample !== "object" || Array.isArray(sample)) return null;
  for (const [k, v] of Object.entries(sample as Record<string, unknown>)) {
    if (Array.isArray(v) && v.some((x) => x && typeof x === "object")) return k;
  }
  return null;
}

/** The representative item to preview/discover from (unwraps array + collection). */
export function sampleItem(sample: unknown, collectionPath: string | null): unknown {
  let node: unknown = sample;
  if (collectionPath) {
    const c = getByPath(sample, collectionPath);
    node = Array.isArray(c) ? c[0] : c;
  } else if (Array.isArray(sample)) {
    node = sample[0];
  }
  return node ?? sample;
}

/** Normalised token set of a field/path name for similarity scoring. */
function tokens(name: string): string[] {
  return name
    .replace(/[._-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/** Cheap name-similarity score in [0,1] — token overlap + leaf-name equality boost. */
export function similarity(a: string, b: string): number {
  const ta = new Set(tokens(a));
  const tb = new Set(tokens(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared++;
  const overlap = shared / Math.max(ta.size, tb.size);
  const leafA = a.split(".").pop() ?? a;
  const boost = leafA.toLowerCase() === b.toLowerCase() ? 0.3 : 0;
  return Math.min(1, overlap + boost);
}

/** Best-matching entity field for a wrapped path, above a threshold. */
export function suggestField(wrappedPath: string, fields: FieldDefinition[]): string | null {
  let best: { name: string; score: number } | null = null;
  for (const f of fields) {
    const s = similarity(wrappedPath, f.name);
    if (!best || s > best.score) best = { name: f.name, score: s };
  }
  return best && best.score >= 0.34 ? best.name : null;
}

/** Client-side preview of applyFieldMap — the materialised entity fields for one item. */
export function previewFields(
  item: unknown,
  fieldMap: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [wrappedPath, entityField] of Object.entries(fieldMap)) {
    const raw = getByPath(item, wrappedPath);
    if (raw === undefined) continue;
    out[entityField] = raw != null && typeof raw === "object" ? JSON.stringify(raw) : raw;
  }
  return out;
}

/** PII-looking field names — surfaced as a privacy warning in the editor (Q-risk). */
const PII_RE = /(email|phone|ssn|social.?security|passport|tax.?id|dob|birth)/i;
export function looksLikePii(name: string): boolean {
  return PII_RE.test(name);
}
